import { useEffect, useMemo } from 'react'
import { QueryClient, useQueryClient } from 'react-query'
import {
  BooleanParam,
  DecodedValueMap,
  NumberParam,
  QueryParamConfigMap,
  SetQuery,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params'
import { useAuth } from 'services/auth'
import {
  CellClassCommaArrayParam,
  SampleTypeParam,
  StringArrayParam,
  TimestampParam,
  withIgnoreBlanks,
} from '../../utils/custom-query-params'
import useRawLayerAvailable from '../../utils/useRawLayerAvailable'
import { CellDisplayOptions } from '../shared/CellImageControl'
import { QueryState } from './cellSearchHelpers'

export const useCellBrowsingQueryParams = (): {
  query: DecodedValueMap<QueryParamConfigMap>
  setQuery: SetQuery<QueryParamConfigMap>
  search: QueryState
  displayOptions: CellDisplayOptions
  queryClient: QueryClient
  searchingAcrossCorpus: boolean
  rawCropEligible: boolean
  rawLayerAvailable: boolean
} => {
  /* This component stores state in the query, except for caching cell counts */
  const [query, setQuery] = useQueryParams({
    predictedClasses: withIgnoreBlanks(CellClassCommaArrayParam),
    predictedProbabilityGreaterThan: withIgnoreBlanks(StringParam),
    sampleType: withIgnoreBlanks(SampleTypeParam),
    sampleId: withIgnoreBlanks(StringParam),
    mixedSampleId: withIgnoreBlanks(StringParam),
    cellId: withIgnoreBlanks(StringParam),
    cellNumber: withIgnoreBlanks(StringParam),
    cellTime: withIgnoreBlanks(StringParam),
    before: withIgnoreBlanks(TimestampParam),
    after: withIgnoreBlanks(TimestampParam),
    morphologyClassifiers: withDefault(StringArrayParam, []),
    morphologyClassifiersProbabilityGreaterThan: withIgnoreBlanks(StringParam),
    runId: withIgnoreBlanks(StringParam),
    // Legacy parameter for backwards compatibility
    runIds: StringArrayParam,
    sortOrderKey: withDefault(withIgnoreBlanks(StringParam), 'TIME_ASC'),
    page: withDefault(withIgnoreBlanks(NumberParam), 0),
    quadrant: withDefault(withIgnoreBlanks(NumberParam), 1),
    sharpen: withDefault(withIgnoreBlanks(BooleanParam), false),
    centerCrop: withDefault(withIgnoreBlanks(BooleanParam), false),
    showRawCrop: withDefault(withIgnoreBlanks(BooleanParam), false),
  })

  // Backwards compatibility: migrate legacy runIds parameter to runId
  useEffect(() => {
    const runIds = query.runIds as string[] | null | undefined
    if (runIds && runIds.length > 0 && !query.runId) {
      // Take the first runId from the legacy runIds array, normalize it
      const firstRunId = runIds[0].trim()
      if (firstRunId) {
        // Update query to use new runId parameter and remove legacy runIds
        setQuery({ runId: firstRunId, runIds: undefined }, 'replaceIn')
      }
    }
  }, [query.runIds, query.runId, setQuery])

  // Backwards compatibility: migrate legacy RUN_ID_ASC sort order to TIME_ASC
  useEffect(() => {
    if (query.sortOrderKey === 'RUN_ID_ASC') {
      setQuery({ sortOrderKey: 'TIME_ASC' }, 'replaceIn')
    }
  }, [query.sortOrderKey, setQuery])

  const search: QueryState = query as QueryState

  // Raw-crop comparison is internal-only. The toggle is offered on every run for
  // internal users; whether it is enabled (and whether tiles request raw crops)
  // is driven by the run-level availability signal. Missing/partial coverage
  // falls back to the masked crop per cell on the API side.
  const { isInternalUser } = useAuth()
  const rawCropEligible = isInternalUser
  const rawLayerAvailable = useRawLayerAvailable(query.runId, isInternalUser).data === true

  const displayOptions: CellDisplayOptions = useMemo(() => {
    return {
      sharpen: search.sharpen,
      centerCrop: search.centerCrop,
      showRawCrop: Boolean(search.showRawCrop) && rawCropEligible && rawLayerAvailable,
    }
  }, [search.sharpen, search.centerCrop, search.showRawCrop, rawCropEligible, rawLayerAvailable])

  const queryClient = useQueryClient()

  // runId is already normalized (trimmed) in CellSearchFilters reducer
  const hasRunIdFilter = Boolean(query.runId)

  return useMemo(
    () => ({
      query,
      setQuery,
      search,
      displayOptions,
      queryClient,
      searchingAcrossCorpus: !hasRunIdFilter,
      rawCropEligible,
      rawLayerAvailable,
    }),
    [
      query,
      setQuery,
      search,
      displayOptions,
      queryClient,
      hasRunIdFilter,
      rawCropEligible,
      rawLayerAvailable,
    ]
  )
}

export default useCellBrowsingQueryParams
